import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Divider,
	Paper,
	Typography,
} from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

type QAItem = {
	question: string;
	answer: string | null;
	questionAt: string;
	answerAt: string | null;
};

type Conversation = {
	conversation_id: string;
	qa: QAItem[];
};

type InstructorMessages = {
	instructor_id: string;
	name: string;
	english_name?: string;
	email: string;
	conversations: Conversation[];
};

type ApiResponse = {
	instructors: InstructorMessages[];
};

const cleanAnswerHtml = (value: string) => {
	if (!value) return '';

	// Strip optional ```html ... ``` fences if they exist
	if (value.startsWith('```')) {
		const firstNewline = value.indexOf('\n');
		const lastFence = value.lastIndexOf('```');
		if (firstNewline !== -1 && lastFence !== -1 && lastFence > firstNewline) {
			return value.substring(firstNewline + 1, lastFence).trim();
		}
	}

	return value;
};

const ExpertChatMessages = () => {
	const [data, setData] = useState<InstructorMessages[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchMessages = async () => {
			setLoading(true);
			setError(null);

			try {
				const response = await httpClient(
					`${constants.API_URL}instructor/expert-chat/messages-grouped`,
					{
						method: 'GET',
					},
				);

				const json = response.json as ApiResponse;
				setData(json?.instructors || []);
			} catch (err: any) {
				setError('לא הצלחנו לטעון את ההודעות');
			} finally {
				setLoading(false);
			}
		};

		fetchMessages();
	}, []);

	return (
		<Box p={3} dir="rtl">
			<Typography variant="h4" mb={2}>
				שאלות צ׳אט מומחים
			</Typography>

			{loading && (
				<Box display="flex" justifyContent="center" mt={4}>
					<CircularProgress />
				</Box>
			)}

			{error && !loading && (
				<Typography color="error" mb={2}>
					{error}
				</Typography>
			)}

			{!loading && !error && data.length === 0 && (
				<Typography color="text.secondary">אין נתונים להצגה.</Typography>
			)}

			<Box display="flex" flexDirection="column" gap={2} mt={2}>
				{data.map((instructor) => (
					<Card key={instructor.instructor_id}>
						<CardHeader
							title={instructor.name}
							subheader={instructor.email}
							sx={{ textAlign: 'right' }}
						/>
						<CardContent sx={{ textAlign: 'right' }}>
							{instructor.conversations.map((conversation) => (
								<Box key={conversation.conversation_id} mb={3}>
									<Typography variant="subtitle2" color="text.secondary" mb={1}>
										שיחה: {conversation.conversation_id}
									</Typography>
									{conversation.qa.map((item, index) => {
										const unanswered = !item.answer;

										return (
											<Paper
												key={`${conversation.conversation_id}_${index}`}
												variant="outlined"
												sx={{
													p: 2,
													mb: 1.5,
													backgroundColor: unanswered ? '#fff3e0' : '#f5f5f5',
												}}
											>
												<Typography variant="body1" fontWeight="bold" mb={0.5}>
													שאלה: {item.question}
												</Typography>
												<Typography variant="caption" color="text.secondary" display="block" mb={1}>
													נשאלה ב:{' '}
													{moment(item.questionAt).isValid()
														? moment(item.questionAt).format('DD/MM/YY HH:mm')
														: item.questionAt}
												</Typography>

												<Divider sx={{ my: 1 }} />

												{item.answer ? (
													<Box>
														<Typography variant="body2" color="primary" mb={0.5}>
															תשובה:
														</Typography>
														<Box
															sx={{ fontSize: 14 }}
															dangerouslySetInnerHTML={{
																__html: cleanAnswerHtml(item.answer),
															}}
														/>
														{item.answerAt && (
															<Typography
																variant="caption"
																color="text.secondary"
																display="block"
																mt={1}
															>
																נענתה ב:{' '}
																{moment(item.answerAt).isValid()
																	? moment(item.answerAt).format('DD/MM/YY HH:mm')
																	: item.answerAt}
															</Typography>
														)}
													</Box>
												) : (
													<Typography variant="body2" color="error">
														עדיין ללא תשובה
													</Typography>
												)}
											</Paper>
										);
									})}
								</Box>
							))}
						</CardContent>
					</Card>
				))}
			</Box>
		</Box>
	);
};

export default ExpertChatMessages;

